import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Bell } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import TruckLogo from './TruckLogo';

interface InstallPWAPromptProps {
  onInstall: () => Promise<boolean>;
  onDismiss: () => void;
}

const InstallPWAPrompt: React.FC<InstallPWAPromptProps> = ({ onInstall, onDismiss }) => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  const handleInstall = async () => {
    setIsInstalling(true);
    try {
      const success = await onInstall();
      if (success) {
        setIsVisible(false);
        setTimeout(onDismiss, 300);
      } else {
        setIsInstalling(false);
      }
    } catch (error) {
      console.error('Error installing PWA:', error);
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(onDismiss, 300);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black bg-opacity-50 animate-fade-in">
      <div
        className={`bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
      >
        <div className="p-6">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X size={24} />
          </button>

          <div className="flex flex-col items-center text-center">
            <div className="mb-4 transform scale-110">
              <TruckLogo size={80} />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('pwa.install.title')}
            </h2>

            <p className="text-gray-600 mb-6">
              {t('pwa.install.description')}
            </p>

            <div className="w-full space-y-3 mb-6">
              <div className="flex items-start gap-3 text-left">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Smartphone size={20} className="text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700 font-medium">
                    {t('pwa.install.benefit1')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 text-left">
                <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Bell size={20} className="text-yellow-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700 font-medium">
                    {t('pwa.install.benefit3')}
                  </p>
                </div>
              </div>
            </div>

            <div className="w-full space-y-3">
              <button
                onClick={handleInstall}
                disabled={isInstalling}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Download size={20} />
                {isInstalling ? t('pwa.install.installing') : t('pwa.install.installButton')}
              </button>

              <button
                onClick={handleDismiss}
                className="w-full text-gray-600 py-2 px-6 rounded-xl font-medium hover:bg-gray-100 transition-all duration-200"
              >
                {t('pwa.install.notNow')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPWAPrompt;
